import { z } from 'zod'

import { clinicInfoSchema as legacyReadableClinicInfoSchema } from '../overview/overview-contracts'

const boundedText = (maximum: number) => z.string().trim().max(maximum)
const phonePattern = /^\+?[0-9() .-]+$/

export function normalizeClinicMainPhone(value: string): string | null {
  const raw = value.trim()
  if (!raw) return null
  const hasPlus = raw.startsWith('+')
  if (
    raw.split('+').length > 2 ||
    (raw.includes('+') && !hasPlus) ||
    !phonePattern.test(raw)
  ) {
    return null
  }
  const digits = [...raw].filter((character) => /\d/.test(character)).join('')
  const normalized = hasPlus
    ? `+${digits}`
    : digits.length === 10
      ? `+1${digits}`
      : digits.length === 11 && digits.startsWith('1')
        ? `+${digits}`
        : null
  return normalized && /^\+[1-9][0-9]{7,14}$/.test(normalized)
    ? normalized
    : null
}

const clinicMainPhoneSchema = boundedText(50).refine(
  (value) => !value || normalizeClinicMainPhone(value) !== null,
  'Enter a valid phone number, including country code when outside North America.'
)

export const clinicInfoSchema = z
  .object({
    dentistryName: boundedText(120).min(1, 'Clinic name is required.'),
    dentistryAddress: boundedText(500),
    dentistryPhone: clinicMainPhoneSchema,
    dentistryEmail: z
      .union([z.literal(''), z.string().trim().email('Enter a valid email.')])
      .refine((value) => value.length <= 254, 'Email is too long.'),
    emergencyPhone: boundedText(50),
  })
  .strict()

// Retained for the pure legacy-read compatibility model tests. Dashboard writes
// use the strict canonical clinicInfoSchema above.
export const clinicProfileSchema = legacyReadableClinicInfoSchema

export const clinicTimeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .superRefine((value, context) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: value }).format(0)
    } catch {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Clinic timezone is invalid.',
      })
    }
  })

export const clinicProfileResponseSchema = z
  .object({
    clinicInfo: clinicInfoSchema,
    timeZone: clinicTimeZoneSchema,
  })
  .strict()

export const clinicInfoPatchSchema = clinicInfoSchema

const timeRangePattern = /^(?:[01]\d|2[0-3]):[0-5]\d-(?:[01]\d|2[0-3]):[0-5]\d$/

export const workingHoursIntervalSchema = z
  .string()
  .regex(timeRangePattern, 'Use a valid 24-hour time range.')
  .refine((value) => {
    if (value === '00:00-00:00') return true
    const start = value.slice(0, 5)
    const end = value.slice(6)
    return start < end
  }, 'Closing time must be later than opening time.')

export const workingHoursSchema = z
  .object({
    sun: workingHoursIntervalSchema,
    mon: workingHoursIntervalSchema,
    tue: workingHoursIntervalSchema,
    wed: workingHoursIntervalSchema,
    thu: workingHoursIntervalSchema,
    fri: workingHoursIntervalSchema,
    sat: workingHoursIntervalSchema,
  })
  .strict()

export const workingHoursPayloadSchema = z
  .object({ workingHours: workingHoursSchema })
  .strict()

export const schedulingDayKeySchema = z.enum([
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
])
const clockTimeSchema = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Use a valid 24-hour time.')
const schedulingRuleNameSchema = boundedText(80).min(1, 'Name is required.')

export const schedulingPolicyBreakSchema = z
  .object({
    name: schedulingRuleNameSchema,
    days: z.array(schedulingDayKeySchema).min(1, 'Choose at least one day.'),
    start: clockTimeSchema,
    end: clockTimeSchema,
  })
  .strict()
  .refine((value) => value.start < value.end, {
    message: 'End time must be later than start time.',
  })

export const schedulingSpecialDaySchema = z
  .discriminatedUnion('status', [
    z
      .object({
        name: schedulingRuleNameSchema,
        date: z.string().date('Choose a valid date.'),
        status: z.literal('CLOSED'),
      })
      .strict(),
    z
      .object({
        name: schedulingRuleNameSchema,
        date: z.string().date('Choose a valid date.'),
        status: z.literal('OPEN'),
        start: clockTimeSchema,
        end: clockTimeSchema,
      })
      .strict(),
  ])
  .superRefine((value, context) => {
    if (value.status === 'OPEN' && value.start >= value.end) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End time must be later than start time.',
        path: ['end'],
      })
    }
  })

export const schedulingPolicyPayloadSchema = z
  .object({
    workingHours: workingHoursSchema,
    breaks: z.array(schedulingPolicyBreakSchema).max(100),
    specialDays: z.array(schedulingSpecialDaySchema).max(730),
  })
  .strict()
  .superRefine((value, context) => {
    const specialDates = new Set<string>()
    value.specialDays.forEach((item, index) => {
      if (specialDates.has(item.date)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Only one special-hours rule is allowed per date.',
          path: ['specialDays', index, 'date'],
        })
      }
      specialDates.add(item.date)
    })
    value.breaks.forEach((item, index) => {
      item.days.forEach((day) => {
        const hours = value.workingHours[day]
        if (
          hours === '00:00-00:00' ||
          item.start < hours.slice(0, 5) ||
          item.end > hours.slice(6)
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Breaks must be inside regular hours for every selected day.',
            path: ['breaks', index],
          })
        }
      })
    })
  })

export const AGE_SCHEDULING_MAX_RULES = 10
export const AGE_SCHEDULING_MAX_WINDOWS = 4
export const AGE_SCHEDULING_MAX_AGE = 130

const ageYearsSchema = z
  .number({ invalid_type_error: 'Enter an age in whole years.' })
  .int('Enter an age in whole years.')
  .min(0, 'Ages start at 0.')
  .max(130, 'Ages must be 130 or less.')

export const ageSchedulingWindowSchema = z
  .object({ start: clockTimeSchema, end: clockTimeSchema })
  .strict()
  .refine((value) => value.start < value.end, {
    message: 'End time must be later than start time.',
    path: ['end'],
  })

export const ageSchedulingRuleSchema = z
  .object({
    minimumYears: ageYearsSchema,
    // Omitted: open-ended ("7 and older").
    maximumYears: ageYearsSchema.optional(),
    windows: z
      .array(ageSchedulingWindowSchema)
      .min(1, 'Add at least one time window.')
      .max(AGE_SCHEDULING_MAX_WINDOWS),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.maximumYears !== undefined &&
      value.minimumYears > value.maximumYears
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The oldest age must be at least the youngest age.',
        path: ['maximumYears'],
      })
    }
    const windows = [...value.windows].sort((left, right) =>
      left.start.localeCompare(right.start)
    )
    windows.slice(1).forEach((window, index) => {
      const previous = windows[index]
      if (previous && previous.end > window.start) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Time windows for one age range must not overlap.',
          path: ['windows'],
        })
      }
    })
  })

function oldestAge(rule: { maximumYears?: number }) {
  return rule.maximumYears ?? AGE_SCHEDULING_MAX_AGE
}

// Optional section of the clinic's canonical scheduling policy. Disabled or
// absent means Flio schedules exactly as it does without it. When enabled,
// the ranges must cover every age: an uncovered age is never unrestricted.
export const ageSchedulingPolicySchema = z
  .object({
    enabled: z.boolean(),
    rules: z.array(ageSchedulingRuleSchema).max(AGE_SCHEDULING_MAX_RULES),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.enabled && value.rules.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add at least one age range to turn this on.',
        path: ['rules'],
      })
    }
    const ranges = value.rules
      .map((rule, index) => ({ ...rule, index }))
      .sort((left, right) => left.minimumYears - right.minimumYears)
    let overlapping = false
    ranges.slice(1).forEach((rule, position) => {
      const previous = ranges[position]
      if (previous && oldestAge(previous) >= rule.minimumYears) {
        overlapping = true
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Age ranges must not overlap.',
          path: ['rules', rule.index, 'minimumYears'],
        })
      }
    })
    const first = ranges[0]
    const last = ranges[ranges.length - 1]
    const gap = ranges.slice(1).some((rule, position) => {
      const previous = ranges[position]
      return (
        previous !== undefined && oldestAge(previous) + 1 !== rule.minimumYears
      )
    })
    if (
      value.enabled &&
      !overlapping &&
      first !== undefined &&
      last !== undefined &&
      (first.minimumYears !== 0 ||
        gap ||
        oldestAge(last) !== AGE_SCHEDULING_MAX_AGE)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Age ranges must cover every age, starting at 0 with no gaps. Leave the last “To age” empty for “and older”.',
        path: ['rules'],
      })
    }
  })

export const workingHoursResponseSchema = z
  .object({
    workingHours: workingHoursSchema,
    timeZone: clinicTimeZoneSchema,
  })
  .strict()

export type ClinicInfo = z.infer<typeof clinicInfoSchema>
export type ClinicProfileResponse = z.infer<typeof clinicProfileResponseSchema>
export type WorkingHours = z.infer<typeof workingHoursSchema>
export type WorkingHoursPayload = z.infer<typeof workingHoursPayloadSchema>
export type WorkingHoursResponse = z.infer<typeof workingHoursResponseSchema>
export type SchedulingDayKey = z.infer<typeof schedulingDayKeySchema>
export type SchedulingPolicyBreak = z.infer<typeof schedulingPolicyBreakSchema>
export type SchedulingSpecialDay = z.infer<typeof schedulingSpecialDaySchema>
export type SchedulingPolicyPayload = z.infer<
  typeof schedulingPolicyPayloadSchema
>
export type AgeSchedulingPolicy = z.infer<typeof ageSchedulingPolicySchema>

export type ClinicProfile = {
  name: string | null
  address: string | null
  phone: string | null
  email: string | null
  emergencyPhone: string | null
  timeZone: string | null
}
